import React, { useState, useEffect } from 'react';

const SCHEMA_TYPES = {
  Article: {
    name: { label: 'Headline', type: 'text' },
    author: { label: 'Author', type: 'text' },
    datePublished: { label: 'Date Published', type: 'date' },
    image: { label: 'Image URL', type: 'text' },
    url: { label: 'URL', type: 'text' },
    description: { label: 'Description', type: 'textarea' },
  },
  Product: {
    name: { label: 'Product Name', type: 'text' },
    image: { label: 'Image URL', type: 'text' },
    description: { label: 'Description', type: 'textarea' },
    sku: { label: 'SKU', type: 'text' },
    brand: { label: 'Brand', type: 'text' },
    offers: { label: 'Price', type: 'text' },
    url: { label: 'URL', type: 'text' },
  },
  Organization: {
    name: { label: 'Organization Name', type: 'text' },
    url: { label: 'URL', type: 'text' },
    logo: { label: 'Logo URL', type: 'text' },
    contactPoint: { label: 'Contact Point', type: 'text' },
    sameAs: { label: 'Social Links (comma separated)', type: 'text' },
  },
  FAQ: {
    questions: { label: 'Questions & Answers (JSON array)', type: 'textarea', placeholder: '[{"question": "Q1", "answer": "A1"}]' },
  },
};

function getDefaultSchema(type) {
  switch (type) {
    case 'Article':
      return { '@type': 'Article', name: '', author: '', datePublished: '', image: '', url: '', description: '' };
    case 'Product':
      return { '@type': 'Product', name: '', image: '', description: '', sku: '', brand: '', offers: '', url: '' };
    case 'Organization':
      return { '@type': 'Organization', name: '', url: '', logo: '', contactPoint: '', sameAs: '' };
    case 'FAQ':
      return { '@type': 'FAQPage', questions: '' };
    default:
      return {};
  }
}

function buildJsonLd(type, values) {
  switch (type) {
    case 'Article':
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: values.name,
        author: { '@type': 'Person', name: values.author },
        datePublished: values.datePublished,
        image: values.image,
        url: values.url,
        description: values.description,
      };
    case 'Product':
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: values.name,
        image: values.image,
        description: values.description,
        sku: values.sku,
        brand: { '@type': 'Brand', name: values.brand },
        offers: {
          '@type': 'Offer',
          price: values.offers,
          priceCurrency: 'USD',
          url: values.url,
        },
      };
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: values.name,
        url: values.url,
        logo: values.logo,
        contactPoint: values.contactPoint,
        sameAs: values.sameAs ? values.sameAs.split(',').map(s => s.trim()) : [],
      };
    case 'FAQ':
      let questions = [];
      try {
        questions = JSON.parse(values.questions || '[]');
      } catch (e) {
        questions = [];
      }
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions.map(q => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: { '@type': 'Answer', text: q.answer },
        })),
      };
    default:
      return {};
  }
}

export default function SchemaEditor({ schema = {}, setSchema }) {
  const [type, setType] = useState(schema['@type'] || 'Article');
  const [values, setValues] = useState({ ...getDefaultSchema(type), ...schema });
  const [copyMsg, setCopyMsg] = useState('');
  const [customSchema, setCustomSchema] = useState(schema.customSchemaJsonLd ? JSON.stringify(schema.customSchemaJsonLd, null, 2) : '');
  const [customSchemaError, setCustomSchemaError] = useState('');

  // Update values when schema or type changes (but do not call setSchema here)
  useEffect(() => {
    setValues({ ...getDefaultSchema(type), ...schema, '@type': type });
    // Only call setSchema when type changes (to reset schema)
    if (schema['@type'] !== type) {
      setSchema({ ...getDefaultSchema(type), '@type': type });
    }
    // eslint-disable-next-line
  }, [type]);

  const handleChange = (field, value) => {
    const newValues = { ...values, [field]: value };
    setValues(newValues);
    setSchema(newValues);
  };

  // Handle custom schema change
  const handleCustomSchemaChange = (e) => {
    const val = e.target.value;
    setCustomSchema(val);
    if (!val.trim()) {
      setCustomSchemaError('');
      setSchema(s => ({ ...s, customSchemaJsonLd: undefined }));
      return;
    }
    try {
      const parsed = JSON.parse(val);
      setCustomSchemaError('');
      setSchema(s => ({ ...s, customSchemaJsonLd: parsed }));
    } catch (err) {
      setCustomSchemaError('Invalid JSON');
      setSchema(s => ({ ...s, customSchemaJsonLd: undefined }));
    }
  };

  const jsonLd = buildJsonLd(type, values);

  const handleValidate = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonLd, null, 2));
      setCopyMsg('Copied to clipboard! Paste into Google Rich Results Test.');
      window.open('https://search.google.com/test/rich-results', '_blank');
      setTimeout(() => setCopyMsg(''), 3000);
    } catch (e) {
      setCopyMsg('Failed to copy. Please copy manually.');
      setTimeout(() => setCopyMsg(''), 3000);
    }
  };

  return (
    <div className="mt-6 border-t pt-6">
      <h4 className="text-lg font-semibold mb-2">Structured Data (Schema.org)</h4>
      <div className="mb-4">
        <label className="block text-sm font-semibold mb-1">Schema Type</label>
        <select
          className="border rounded px-3 py-2 w-full"
          value={type}
          onChange={e => setType(e.target.value)}
        >
          {Object.keys(SCHEMA_TYPES).map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(SCHEMA_TYPES[type]).map(([field, config]) => (
          <div key={field} className="mb-2">
            <label className="block text-sm font-semibold mb-1">{config.label}</label>
            {config.type === 'textarea' ? (
              <textarea
                className="border rounded px-3 py-2 w-full"
                value={values[field] || ''}
                onChange={e => handleChange(field, e.target.value)}
                placeholder={config.placeholder || ''}
                rows={3}
              />
            ) : (
              <input
                className="border rounded px-3 py-2 w-full"
                type={config.type}
                value={values[field] || ''}
                onChange={e => handleChange(field, e.target.value)}
                placeholder={config.placeholder || ''}
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-6">
        <label className="block text-sm font-semibold mb-1">Custom Schema (JSON-LD)</label>
        <textarea
          className="border rounded px-3 py-2 w-full font-mono text-xs"
          rows={5}
          value={customSchema}
          onChange={handleCustomSchemaChange}
          placeholder='Paste custom JSON-LD here (e.g., FAQPage, Product, etc.)'
        />
        {customSchemaError && (
          <div className="text-red-600 text-xs mt-1">{customSchemaError}</div>
        )}
        <div className="text-xs text-gray-500 mt-1">If provided, this will override the default schema for this page/product.</div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-semibold mb-1">JSON-LD Preview</label>
        <pre className="bg-gray-100 rounded p-3 text-xs overflow-x-auto" style={{ maxHeight: 200 }}>
          {JSON.stringify(jsonLd, null, 2)}
        </pre>
        <button
          type="button"
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition"
          onClick={handleValidate}
        >
          Validate with Google
        </button>
        {copyMsg && (
          <div className="mt-2 text-green-700 text-sm font-semibold">{copyMsg}</div>
        )}
      </div>
    </div>
  );
} 